import PropTypes from 'prop-types'
import {Radio} from '@pepsico-ds/ui'
import {SIZE, ORIENTATION} from '@/shared/constants/globalStyle'


/**
 * RadioButton component renders a group of radio buttons.
 *
 * @param {Object[]} items - Array of items to be displayed as radio buttons.
 * @param {string} [reference='radio-button'] - Reference identifier for the radio button group.
 * @param {string} [label=''] - Label for the radio button group.
 * @param {string} [helperText=''] - Helper text to be displayed below the radio button group.
 * @param {function} onUpdate - Callback function to handle the update event.
 * @param {('small'|'large')} [size='small'] - Size of the radio buttons (e.g., 'small', 'medium', 'large').
 * @param {boolean} [disabled=false] - Flag to disable the radio buttons.
 * @param {boolean} [isDark=false] - Flag to apply dark theme styling.
 * @param {number|null} [selectedItemIndex=null] - Index of the currently selected item.
 * @param {string|null} [feedbackType=null] - Type of feedback to be displayed (e.g., 'error', 'warning', 'info').
 * @param {boolean} selected - Flag to indicate if the radio button is selected.
 * @param {string} className - Additional class names for custom styling.
 * @param {string} [id=''] - Optional additional id to apply to the radio button.
 * @returns {JSX.Element} The rendered RadioButton component.
 */
const RadioButton = ({
  items,
  reference = 'radio-button',
  label = '',
  helperText = '',
  onUpdate,
  size = SIZE.SMALL,
  disabled = false,
  isDark = false,
  selectedItemIndex = null,
  feedbackType = null,
  selected,
  className,
  orientation = ORIENTATION.HORIZONTAL,
  id,
}) => {
  return (
    <Radio
      items={items}
      reference={reference}
      label={label}
      helperText={helperText}
      onUpdate={onUpdate}
      size={size}
      disabled={disabled}
      isDark={isDark}
      selectedItemIndex={selectedItemIndex}
      feedbackType={feedbackType}
      selected={selected}
      className={className}
      orientation={orientation}
      id={id}
    />
  )
}

RadioButton.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  reference: PropTypes.string,
  label: PropTypes.string,
  helperText: PropTypes.string,
  onUpdate: PropTypes.func,
  size: PropTypes.oneOf(Object.values(SIZE)),
  disabled: PropTypes.bool,
  isDark: PropTypes.bool,
  selectedItemIndex: PropTypes.number,
  feedbackType: PropTypes.string,
  selected: PropTypes.bool,
  className: PropTypes.string,
  orientation: PropTypes.oneOf(Object.values(ORIENTATION)),
  id: PropTypes.string,
}

export default RadioButton
